<script lang="ts">
  import { router } from "@inertiajs/svelte";
  import { checklistIcons } from "../../lib/icons";
  import type { Trip } from "../../lib/types";
  import { addNotification } from "../../lib/stores";
  import Layout from "../../components/layout/Layout.svelte";
  import TripSubNav from "../../components/navigation/TripSubNav.svelte";
  import Modal from "../../components/overlay/Modal.svelte";
  import Button from "../../components/input/Button.svelte";

  interface ChecklistItem {
    id: string;
    name: string;
    category: string;
    category_label: string;
    is_checked: boolean;
    quantity: number;
  }

  let { trip, checklistItems = [], categories = {}, templates = {}, user = null }: {
    trip: Trip;
    checklistItems?: ChecklistItem[];
    categories?: Record<string, string>;
    templates?: Record<string, string[]>;
    user?: { id: string; name: string; email: string; avatarUrl?: string } | null;
  } = $props();

  let showAddModal = $state(false);
  let showTemplateModal = $state(false);
  let newItemName = $state("");
  let newItemCategory = $state("essentials");
  let newItemQuantity = $state(1);

  // 카테고리별 그룹핑
  let groupedItems = $derived(checklistItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, ChecklistItem[]>));

  // 진행률 계산
  let totalItems = $derived(checklistItems.length);
  let checkedItems = $derived(checklistItems.filter(i => i.is_checked).length);
  let progressPercentage = $derived(totalItems > 0 ? Math.round((checkedItems / totalItems) * 100) : 0);

  async function toggleItem(item: ChecklistItem) {
    try {
      const response = await fetch(`/api/v1/trips/${trip.id}/checklist_items/${item.id}/toggle`, {
        method: "POST"
      });

      if (response.ok) {
        item.is_checked = !item.is_checked;
        checklistItems = [...checklistItems];
      }
    } catch (error) {
      addNotification("error", "업데이트에 실패했습니다.");
    }
  }

  async function addItem() {
    if (!newItemName.trim()) return;

    try {
      const response = await fetch(`/api/v1/trips/${trip.id}/checklist_items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          checklist_item: {
            name: newItemName,
            category: newItemCategory,
            quantity: newItemQuantity
          }
        })
      });

      if (response.ok) {
        addNotification("success", "아이템이 추가되었습니다.");
        newItemName = "";
        newItemQuantity = 1;
        showAddModal = false;
        router.reload();
      }
    } catch (error) {
      addNotification("error", "추가에 실패했습니다.");
    }
  }

  async function deleteItem(id: string) {
    try {
      const response = await fetch(`/api/v1/trips/${trip.id}/checklist_items/${id}`, {
        method: "DELETE"
      });

      if (response.ok) {
        checklistItems = checklistItems.filter(i => i.id !== id);
        addNotification("success", "아이템이 삭제되었습니다.");
      }
    } catch (error) {
      addNotification("error", "삭제에 실패했습니다.");
    }
  }

  async function addFromTemplate(category: string) {
    try {
      const response = await fetch(`/api/v1/trips/${trip.id}/checklist_items/from_template`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category })
      });

      if (response.ok) {
        const data = await response.json();
        addNotification("success", `${data.data.created_count}개 아이템이 추가되었습니다.`);
        showTemplateModal = false;
        router.reload();
      }
    } catch (error) {
      addNotification("error", "템플릿 추가에 실패했습니다.");
    }
  }
</script>

<Layout {user}>
  <div class="min-h-screen bg-app">
    <TripSubNav {trip} currentPage="checklist" />

    <main class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-2xl font-bold text-primary">준비물 체크리스트</h1>
          <p class="text-secondary">여행 준비물을 체크하세요</p>
        </div>
        <div class="flex gap-2">
          <Button variant="secondary" onclick={() => showTemplateModal = true}>템플릿</Button>
          <Button variant="primary" onclick={() => showAddModal = true}>+ 아이템 추가</Button>
        </div>
      </div>

      <!-- Progress Bar -->
      <div class="bg-surface rounded-2xl p-6 shadow-sm border border-default mb-6">
        <div class="flex items-center justify-between mb-3">
          <span class="text-sm font-medium text-secondary">진행률</span>
          <span class="text-sm font-bold text-blue-600">{checkedItems}/{totalItems} ({progressPercentage}%)</span>
        </div>
        <div class="h-3 bg-surface-hover rounded-full overflow-hidden">
          <div
            class="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full transition-all duration-500"
            style="width: {progressPercentage}%"
          ></div>
        </div>
      </div>

      <!-- Checklist by Category -->
      {#if totalItems === 0}
        <div class="bg-surface rounded-2xl p-12 text-center shadow-sm border border-default">
          <div class="mx-auto w-16 h-16 mb-4 flex items-center justify-center rounded-full" style="background: rgba(255,255,255,0.06);">
            <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: var(--text-muted);">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-primary mb-2">체크리스트가 비어있습니다</h3>
          <p class="text-muted mb-6">템플릿으로 빠르게 시작하거나 직접 아이템을 추가하세요</p>
          <div class="flex gap-3 justify-center">
            <Button variant="secondary" onclick={() => showTemplateModal = true}>템플릿으로 시작</Button>
            <Button variant="primary" onclick={() => showAddModal = true}>직접 추가</Button>
          </div>
        </div>
      {:else}
        <div class="space-y-6">
          {#each Object.entries(groupedItems) as [category, items]}
            <div class="bg-surface rounded-2xl shadow-sm border border-default overflow-hidden">
              <!-- Category Header -->
              <div class="px-6 py-4 bg-app border-b border-default flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 flex items-center justify-center rounded-full" style="background: rgba(32,178,170,0.15);">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: var(--accent);">
                      {@html checklistIcons[category] ?? checklistIcons.other}
                    </svg>
                  </div>
                  <div>
                    <h3 class="font-semibold text-primary">{categories[category] || category}</h3>
                    <p class="text-xs text-muted">
                      {items.filter(i => i.is_checked).length}/{items.length} 완료
                    </p>
                  </div>
                </div>
                <div class="w-24 h-2 bg-surface-hover rounded-full overflow-hidden">
                  <div
                    class="h-full bg-green-500 rounded-full"
                    style="width: {items.length > 0 ? (items.filter(i => i.is_checked).length / items.length * 100) : 0}%"
                  ></div>
                </div>
              </div>

              <!-- Items -->
              <div class="divide-y divide-border">
                {#each items as item}
                  <div class="px-6 py-3 flex items-center gap-4 hover:bg-surface-hover transition-colors group">
                    <button
                      onclick={() => toggleItem(item)}
                      class="w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all
                        {item.is_checked 
                          ? 'bg-green-500 border-green-500 text-white' 
                          : 'border-muted hover:border-green-500'}"
                    >
                      {#if item.is_checked}
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                        </svg>
                      {/if}
                    </button>
                    
                    <span class="flex-1 {item.is_checked ? 'text-muted line-through' : 'text-primary'}">
                      {item.name}
                      {#if item.quantity > 1}
                        <span class="text-xs text-muted ml-1">x{item.quantity}</span>
                      {/if}
                    </span>

                    <button
                      aria-label="아이템 삭제"
                      onclick={() => deleteItem(item.id)}
                      class="opacity-0 group-hover:opacity-100 p-1 text-muted hover:text-error transition-all"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                {/each}
              </div>
            </div>
          {/each}
        </div>
      {/if}
    </main>

    <!-- Add Item Modal -->
    <Modal show={showAddModal} title="아이템 추가" onClose={() => showAddModal = false}>
      <form onsubmit={(e) => { e.preventDefault(); addItem(); }} class="space-y-4">
        <div>
          <label for="itemName" class="label">아이템명</label>
          <input
            id="itemName"
            type="text"
            bind:value={newItemName}
            class="input"
            placeholder="예: 여권, 충전기"
            required
          />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label for="itemCategory" class="label">카테고리</label>
            <select id="itemCategory" bind:value={newItemCategory} class="input">
              {#each Object.entries(categories) as [key, label]}
                <option value={key}>{label}</option>
              {/each}
            </select>
          </div>
          <div>
            <label for="itemQuantity" class="label">수량</label>
            <input
              id="itemQuantity"
              type="number"
              bind:value={newItemQuantity}
              class="input"
              min="1"
            />
          </div>
        </div>

        <div class="flex gap-3 pt-4">
          <Button type="button" variant="secondary" class="flex-1" onclick={() => showAddModal = false}>취소</Button>
          <Button type="submit" variant="primary" class="flex-1">추가하기</Button>
        </div>
      </form>
    </Modal>

    <!-- Template Modal -->
    <Modal show={showTemplateModal} title="템플릿으로 추가" size="lg" onClose={() => showTemplateModal = false}>
      <p class="text-secondary mb-4">카테고리를 선택하면 기본 아이템이 자동으로 추가됩니다.</p>
      
      <div class="grid grid-cols-2 gap-3">
        {#each Object.entries(templates) as [category, items]}
          <button
            onclick={() => addFromTemplate(category)}
            class="p-4 bg-app rounded-xl hover:bg-blue-50 hover:border-blue-200 border-2 border-transparent transition-all text-left"
          >
            <div class="flex items-center gap-2 mb-2">
              <div class="w-8 h-8 flex items-center justify-center rounded-full" style="background: rgba(32,178,170,0.15);">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: var(--accent);">
                  {@html checklistIcons[category] ?? checklistIcons.other}
                </svg>
              </div>
              <span class="font-semibold text-primary">{categories[category] || category}</span>
            </div>
            <p class="text-xs text-muted">{items.slice(0, 3).join(", ")}...</p>
            <p class="text-xs text-blue-600 mt-1">{items.length}개 아이템</p>
          </button>
        {/each}
      </div>

      <div class="mt-6 pt-4 border-t border-default">
        <Button variant="secondary" fullWidth onclick={() => showTemplateModal = false}>닫기</Button>
      </div>
    </Modal>
  </div>
</Layout>
